import React, { useState, useMemo } from 'react';
import {
    useTranslate,
    useGetList,
} from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Typography,
    ToggleButton,
    ToggleButtonGroup,
    Chip,
    IconButton,
    Alert,
    Tooltip,
    useMediaQuery,
    useTheme,
    CircularProgress,
    TextField,
    MenuItem,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Button,
    Divider,
} from '@mui/material';
import {
    CalendarMonth,
    ChevronLeft,
    ChevronRight,
    Today,
    Close,
    OpenInNew,
    Person,
    Place,
    EvStation,
    Schedule,
    Payments,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { ReservationStatusChip } from './ReservationsList';

type ViewRange = 'day' | 'week';

const STATUS_COLORS: Record<string, string> = {
    confirmed: '#4CAF50',
    active: '#1976D2',
    completed: '#1B5E20',
    no_show: '#D32F2F',
    cancelled: '#9E9E9E',
};

const dayKey = (d: Date): string => {
    const y = d.getFullYear();
    const m = String(d.getMonth() + 1).padStart(2, '0');
    const day = String(d.getDate()).padStart(2, '0');
    return `${y}-${m}-${day}`;
};

const startOfDay = (d: Date) => new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
const addDays = (d: Date, n: number) => new Date(d.getTime() + n * 24 * 60 * 60 * 1000);
const startOfWeek = (d: Date) => {
    const day = d.getDay(); // 0 = Sun
    const diff = (day === 0 ? -6 : 1 - day); // Make Mon = start
    return startOfDay(addDays(d, diff));
};

interface Reservation {
    id: string;
    site_id?: string;
    siteId?: string;
    site_name?: string | null;
    siteName?: string | null;
    charger_id?: string;
    chargerId?: string;
    charger_name?: string | null;
    chargerName?: string | null;
    connector_id?: number;
    connectorId?: number;
    start_time?: string;
    startTime?: string;
    slot_end_at?: string;
    slotEndAt?: string;
    hold_until?: string;
    holdUntil?: string;
    duration_minutes?: number;
    durationMinutes?: number;
    status: string;
    fee_amount?: number | null;
    feeAmount?: number | null;
    fee_currency?: string | null;
    feeCurrency?: string | null;
    fee_policy?: string;
    feePolicy?: string;
    reservation_mode?: string;
    reservationMode?: string;
    user_id?: string;
    userId?: string;
    user_email?: string | null;
    userEmail?: string | null;
    user_name?: string | null;
    userName?: string | null;
    // Legacy nested shapes (older API responses)
    app_user_id?: string;
    appUserId?: string;
    app_user?: { email?: string; first_name?: string; last_name?: string };
    appUser?: { email?: string; firstName?: string; lastName?: string };
}

// Pull the display name out of whichever field the API populated. Prefers a
// real human name over an email, and email over an opaque id, so the calendar
// block reads as "Alice Chan" rather than "user_9aaeffa9...".
function getDisplayName(r: Reservation): string {
    const name =
        r.user_name ||
        r.userName ||
        (r.app_user && r.app_user.first_name
            ? `${r.app_user.first_name} ${r.app_user.last_name ?? ''}`.trim()
            : '') ||
        (r.appUser && r.appUser.firstName
            ? `${r.appUser.firstName} ${r.appUser.lastName ?? ''}`.trim()
            : '');
    if (name) return name;
    const email =
        r.user_email ||
        r.userEmail ||
        (r.app_user && r.app_user.email) ||
        (r.appUser && r.appUser.email);
    if (email) return email;
    return r.user_id || r.userId || r.app_user_id || r.appUserId || r.id;
}

function getEmail(r: Reservation): string | null {
    return (
        r.user_email ||
        r.userEmail ||
        (r.app_user && r.app_user.email) ||
        (r.appUser && r.appUser.email) ||
        null
    );
}

function getSiteName(r: Reservation): string | null {
    return r.site_name ?? r.siteName ?? null;
}

function getChargerName(r: Reservation): string | null {
    return r.charger_name ?? r.chargerName ?? null;
}

function getConnectorId(r: Reservation): number | null {
    return r.connector_id ?? r.connectorId ?? null;
}

function getStartDate(r: Reservation): Date | null {
    const iso = r.start_time || r.startTime;
    if (!iso) return null;
    const d = new Date(iso);
    return Number.isNaN(d.getTime()) ? null : d;
}

function getEndDate(r: Reservation): Date | null {
    const iso = r.slot_end_at || r.slotEndAt;
    if (iso) {
        const d = new Date(iso);
        if (!Number.isNaN(d.getTime())) return d;
    }
    // Fall back to start + duration so the modal/tooltip still shows a window
    // for older rows that don't have slot_end_at populated.
    const start = getStartDate(r);
    const minutes = r.duration_minutes ?? r.durationMinutes;
    if (start && typeof minutes === 'number') {
        return new Date(start.getTime() + minutes * 60_000);
    }
    return null;
}

function formatTimeRange(start: Date | null, end: Date | null): string {
    if (!start) return '—';
    const fmt = (d: Date) => d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
    if (!end) return fmt(start);
    return `${fmt(start)} – ${fmt(end)}`;
}

function formatFee(amount: number | null | undefined, currency: string | null | undefined): string | null {
    if (amount === null || amount === undefined) return null;
    const c = currency || 'HKD';
    return `${c} ${Number(amount).toFixed(2)}`;
}

const TIMELINE_HOURS = 24;
const ROW_HEIGHT = 60;

const fmtTime = (d: Date) => `${String(d.getHours()).padStart(2, '0')}:00`;

interface BlockProps {
    reservation: Reservation;
    rowHeight?: number;
    onOpen: (r: Reservation) => void;
}

const Block: React.FC<BlockProps> = ({ reservation, rowHeight = ROW_HEIGHT, onOpen }) => {
    const translate = useTranslate();
    const start = getStartDate(reservation);
    if (!start) return null;
    const durationMin =
        reservation.duration_minutes ?? reservation.durationMinutes ?? 60;
    const top = (start.getMinutes() / 60) * rowHeight;
    const height = (durationMin / 60) * rowHeight;
    const bg = STATUS_COLORS[reservation.status] || '#9E9E9E';

    const displayName = getDisplayName(reservation);
    const email = getEmail(reservation);
    const siteName = getSiteName(reservation);
    const chargerName = getChargerName(reservation);
    const connectorId = getConnectorId(reservation);
    const end = getEndDate(reservation);
    const feeAmount = reservation.fee_amount ?? reservation.feeAmount;
    const feeCurrency = reservation.fee_currency ?? reservation.feeCurrency;
    const feeLabel = formatFee(feeAmount, feeCurrency);
    const mode = reservation.reservation_mode || reservation.reservationMode;
    const feePolicy = reservation.fee_policy || reservation.feePolicy;

    const blockHeight = Math.max(height, 22);
    // Show two lines only when the block is tall enough to fit them without
    // squishing — otherwise truncate to a single line so short blocks stay
    // legible.
    const showSecondLine = blockHeight >= 36;

    return (
        <Tooltip
            arrow
            placement="right"
            componentsProps={{
                tooltip: {
                    sx: {
                        bgcolor: 'rgba(33, 33, 33, 0.96)',
                        maxWidth: 320,
                        p: 1.25,
                    },
                },
            }}
            title={
                <Box sx={{ minWidth: 220 }}>
                    {/* Header: status + time range */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.75 }}>
                        <Box
                            sx={{
                                width: 8,
                                height: 8,
                                borderRadius: '50%',
                                backgroundColor: bg,
                                flexShrink: 0,
                            }}
                        />
                        <Typography variant="caption" sx={{ fontWeight: 700, color: 'white' }}>
                            {translate(`resources.reservations.statuses.${reservation.status}`, { _: reservation.status })}
                        </Typography>
                        <Typography
                            variant="caption"
                            sx={{ color: 'rgba(255,255,255,0.75)', ml: 'auto', fontVariantNumeric: 'tabular-nums' }}
                        >
                            {formatTimeRange(start, end)}
                        </Typography>
                    </Box>
                    <Divider sx={{ borderColor: 'rgba(255,255,255,0.15)', mb: 0.75 }} />

                    {/* User */}
                    <Box sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                        <Person sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', mt: 0.25 }} />
                        <Box>
                            <Typography variant="caption" sx={{ color: 'white', display: 'block', lineHeight: 1.3 }}>
                                {displayName}
                            </Typography>
                            {email && email !== displayName && (
                                <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.65)', display: 'block', fontSize: '0.65rem' }}>
                                    {email}
                                </Typography>
                            )}
                        </Box>
                    </Box>

                    {/* Site */}
                    {siteName && (
                        <Box sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                            <Place sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', mt: 0.25 }} />
                            <Typography variant="caption" sx={{ color: 'white', lineHeight: 1.3 }}>
                                {siteName}
                            </Typography>
                        </Box>
                    )}

                    {/* Charger / connector */}
                    {(chargerName || connectorId !== null) && (
                        <Box sx={{ display: 'flex', gap: 0.75, mb: 0.5 }}>
                            <EvStation sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', mt: 0.25 }} />
                            <Typography variant="caption" sx={{ color: 'white', lineHeight: 1.3 }}>
                                {chargerName || '—'}
                                {connectorId !== null && ` · ${translate('resources.reservations.fields.connectorId')} ${connectorId}`}
                            </Typography>
                        </Box>
                    )}

                    {/* Mode + fee policy */}
                    {(mode || feePolicy || feeLabel) && (
                        <Box sx={{ display: 'flex', gap: 0.75, mt: 0.25 }}>
                            <Payments sx={{ fontSize: 14, color: 'rgba(255,255,255,0.7)', mt: 0.25 }} />
                            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.85)', lineHeight: 1.3 }}>
                                {[
                                    mode && translate(`resources.reservations.modes.${mode}`, { _: mode }),
                                    feePolicy && translate(`resources.reservations.feePolicies.${feePolicy}`, { _: feePolicy }),
                                    feeLabel,
                                ]
                                    .filter(Boolean)
                                    .join(' · ')}
                            </Typography>
                        </Box>
                    )}

                    {/* ID footer for support reference */}
                    <Typography
                        variant="caption"
                        sx={{
                            color: 'rgba(255,255,255,0.45)',
                            display: 'block',
                            mt: 0.75,
                            fontSize: '0.6rem',
                            fontFamily: 'monospace',
                        }}
                    >
                        {reservation.id}
                    </Typography>
                </Box>
            }
        >
            <Box
                onClick={() => onOpen(reservation)}
                sx={{
                    position: 'absolute',
                    top,
                    left: 2,
                    right: 2,
                    height: blockHeight,
                    backgroundColor: bg,
                    color: 'white',
                    fontSize: '0.65rem',
                    px: 0.6,
                    py: 0.25,
                    borderRadius: 0.75,
                    cursor: 'pointer',
                    overflow: 'hidden',
                    boxShadow: '0 1px 2px rgba(0,0,0,0.2)',
                    '&:hover': { opacity: 0.9, transform: 'translateY(-1px)' },
                    transition: 'transform 120ms ease-out, opacity 120ms ease-out',
                    zIndex: 2,
                }}
            >
                <Typography
                    variant="caption"
                    sx={{
                        display: 'block',
                        fontWeight: 700,
                        fontSize: '0.65rem',
                        lineHeight: 1.15,
                        whiteSpace: 'nowrap',
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                    }}
                >
                    {displayName}
                </Typography>
                {showSecondLine && chargerName && (
                    <Typography
                        variant="caption"
                        sx={{
                            display: 'block',
                            fontSize: '0.6rem',
                            opacity: 0.85,
                            lineHeight: 1.15,
                            whiteSpace: 'nowrap',
                            overflow: 'hidden',
                            textOverflow: 'ellipsis',
                        }}
                    >
                        {chargerName}
                    </Typography>
                )}
            </Box>
        </Tooltip>
    );
};

interface DetailsModalProps {
    reservation: Reservation | null;
    onClose: () => void;
}

const ReservationDetailsModal: React.FC<DetailsModalProps> = ({ reservation, onClose }) => {
    const translate = useTranslate();
    const navigate = useNavigate();
    if (!reservation) return null;

    const start = getStartDate(reservation);
    const end = getEndDate(reservation);
    const displayName = getDisplayName(reservation);
    const email = getEmail(reservation);
    const siteName = getSiteName(reservation);
    const chargerName = getChargerName(reservation);
    const connectorId = getConnectorId(reservation);
    const feeAmount = reservation.fee_amount ?? reservation.feeAmount;
    const feeCurrency = reservation.fee_currency ?? reservation.feeCurrency;
    const feeLabel = formatFee(feeAmount, feeCurrency);
    const mode = reservation.reservation_mode || reservation.reservationMode;
    const feePolicy = reservation.fee_policy || reservation.feePolicy;
    const dateLabel = start
        ? start.toLocaleDateString(undefined, { weekday: 'long', month: 'short', day: 'numeric', year: 'numeric' })
        : null;

    return (
        <Dialog
            open
            onClose={onClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{ sx: { borderRadius: 2 } }}
        >
            <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1.5, pr: 6 }}>
                <ReservationStatusChip status={reservation.status} />
                <Typography variant="subtitle1" sx={{ fontFamily: 'monospace', color: 'text.secondary', flexShrink: 0 }}>
                    {reservation.id}
                </Typography>
                <IconButton
                    aria-label={translate('ra.action.close')}
                    onClick={onClose}
                    sx={{ position: 'absolute', right: 8, top: 8 }}
                    size="small"
                >
                    <Close />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers sx={{ pt: 2 }}>
                {/* When */}
                <DetailRow icon={<Schedule fontSize="small" />} label={translate('resources.reservations.fields.startTime')}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {dateLabel || '—'}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ fontVariantNumeric: 'tabular-nums' }}>
                        {formatTimeRange(start, end)}
                    </Typography>
                </DetailRow>

                {/* Who */}
                <DetailRow icon={<Person fontSize="small" />} label={translate('resources.reservations.fields.appUserId')}>
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {displayName}
                    </Typography>
                    {email && email !== displayName && (
                        <Typography variant="body2" color="text.secondary">
                            {email}
                        </Typography>
                    )}
                </DetailRow>

                {/* Where */}
                <DetailRow icon={<Place fontSize="small" />} label={translate('resources.reservations.fields.siteId')}>
                    <Typography variant="body2">{siteName || '—'}</Typography>
                </DetailRow>

                {(chargerName || connectorId !== null) && (
                    <DetailRow icon={<EvStation fontSize="small" />} label={translate('resources.reservations.fields.chargerId')}>
                        <Typography variant="body2">
                            {chargerName || '—'}
                            {connectorId !== null && (
                                <Typography component="span" color="text.secondary">
                                    {' · '}
                                    {translate('resources.reservations.fields.connectorId')} {connectorId}
                                </Typography>
                            )}
                        </Typography>
                    </DetailRow>
                )}

                {/* How (mode + fees) */}
                {(mode || feePolicy || feeLabel) && (
                    <DetailRow icon={<Payments fontSize="small" />} label={translate('resources.reservations.fields.feePolicy')}>
                        {mode && (
                            <Typography variant="body2">
                                {translate(`resources.reservations.modes.${mode}`, { _: mode })}
                            </Typography>
                        )}
                        {feePolicy && (
                            <Typography variant="body2" color="text.secondary">
                                {translate(`resources.reservations.feePolicies.${feePolicy}`, { _: feePolicy })}
                                {feeLabel ? ` · ${feeLabel}` : ''}
                            </Typography>
                        )}
                    </DetailRow>
                )}
            </DialogContent>
            <DialogActions sx={{ px: 3, py: 1.5 }}>
                <Button onClick={onClose} color="inherit">
                    {translate('ra.action.close')}
                </Button>
                <Button
                    onClick={() => {
                        navigate(`/reservations/${reservation.id}/show`);
                    }}
                    variant="contained"
                    startIcon={<OpenInNew />}
                    sx={{ backgroundColor: '#009A75', '&:hover': { backgroundColor: '#007a5d' } }}
                >
                    {translate('resources.reservations.calendar.openDetail')}
                </Button>
            </DialogActions>
        </Dialog>
    );
};

interface DetailRowProps {
    icon: React.ReactNode;
    label: string;
    children: React.ReactNode;
}

const DetailRow: React.FC<DetailRowProps> = ({ icon, label, children }) => (
    <Box sx={{ display: 'flex', gap: 1.5, mb: 2 }}>
        <Box
            sx={{
                width: 32,
                height: 32,
                borderRadius: 1,
                backgroundColor: 'rgba(0,154,117,0.08)',
                color: '#009A75',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                flexShrink: 0,
                mt: 0.25,
            }}
        >
            {icon}
        </Box>
        <Box sx={{ flex: 1, minWidth: 0 }}>
            <Typography variant="caption" sx={{ color: 'text.secondary', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 600 }}>
                {label}
            </Typography>
            <Box sx={{ mt: 0.25 }}>{children}</Box>
        </Box>
    </Box>
);

const Legend: React.FC = () => {
    const translate = useTranslate();
    return (
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
            <Typography variant="caption" sx={{ mr: 1 }}>
                {translate('resources.reservations.calendar.legend')}:
            </Typography>
            {[
                { key: 'confirmed', label: 'legendConfirmed' },
                { key: 'active', label: 'legendActive' },
                { key: 'no_show', label: 'legendNoShow' },
                { key: 'completed', label: 'legendCompleted' },
                { key: 'cancelled', label: 'legendCancelled' },
            ].map((s) => (
                <Chip
                    key={s.key}
                    size="small"
                    label={translate(`resources.reservations.calendar.${s.label}`)}
                    sx={{ backgroundColor: STATUS_COLORS[s.key], color: 'white', fontSize: '0.7rem' }}
                />
            ))}
        </Box>
    );
};

export const CalendarView: React.FC = () => {
    const translate = useTranslate();
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('md'));

    const [siteId, setSiteId] = useState<string>('');
    const [chargerId, setChargerId] = useState<string>('');
    const [viewRange, setViewRange] = useState<ViewRange>('week');
    const [anchorDate, setAnchorDate] = useState<Date>(startOfDay(new Date()));
    const [selected, setSelected] = useState<Reservation | null>(null);

    const range = useMemo(() => {
        if (viewRange === 'day') {
            const from = startOfDay(anchorDate);
            const to = addDays(from, 1);
            return { from, to };
        }
        const from = startOfWeek(anchorDate);
        const to = addDays(from, 7);
        return { from, to };
    }, [anchorDate, viewRange]);

    const filters = useMemo(() => {
        const f: any = {
            start_time_from: range.from.toISOString(),
            start_time_to: range.to.toISOString(),
        };
        if (siteId) f.site_id = siteId;
        if (chargerId) f.charger_id = chargerId;
        return f;
    }, [siteId, chargerId, range]);

    const { data, isLoading } = useGetList(
        'reservations',
        {
            filter: filters,
            sort: { field: 'start_time', order: 'ASC' },
            pagination: { page: 1, perPage: 500 },
        },
        { enabled: !!siteId }
    );

    const { data: sites, isLoading: sitesLoading } = useGetList('sites', {
        pagination: { page: 1, perPage: 500 },
        sort: { field: 'name', order: 'ASC' },
    });

    const { data: chargers, isLoading: chargersLoading } = useGetList(
        'chargers',
        {
            pagination: { page: 1, perPage: 500 },
            filter: siteId ? { site_id: siteId } : {},
            sort: { field: 'name', order: 'ASC' },
        },
        { enabled: !!siteId },
    );

    const rangeLabel = useMemo(() => {
        const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric', year: 'numeric' };
        if (viewRange === 'day') return range.from.toLocaleDateString(undefined, opts);
        const end = addDays(range.from, 6);
        return `${range.from.toLocaleDateString(undefined, { month: 'short', day: 'numeric' })} – ${end.toLocaleDateString(undefined, opts)}`;
    }, [range.from, viewRange]);

    const visible: Reservation[] = useMemo(() => {
        if (!data) return [];
        return data
            .map((r: any) => r as Reservation)
            .filter((r) => ['confirmed', 'active', 'no_show', 'completed', 'cancelled'].includes(r.status));
    }, [data]);

    const days = useMemo(() => {
        const list: Date[] = [];
        const count = viewRange === 'day' ? 1 : 7;
        for (let i = 0; i < count; i++) list.push(addDays(range.from, i));
        return list;
    }, [range.from, viewRange]);

    const blocksByDayHour = useMemo(() => {
        const map: Record<string, Record<number, Reservation[]>> = {};
        days.forEach((d) => {
            map[dayKey(d)] = {};
            for (let h = 0; h < TIMELINE_HOURS; h++) map[dayKey(d)][h] = [];
        });
        visible.forEach((r) => {
            const s = new Date(r.start_time || r.startTime || '');
            const k = dayKey(s);
            const h = s.getHours();
            if (map[k] && map[k][h]) map[k][h].push(r);
        });
        return map;
    }, [visible, days]);

    const handlePrev = () => {
        setAnchorDate((prev) => addDays(prev, viewRange === 'day' ? -1 : -7));
    };
    const handleNext = () => {
        setAnchorDate((prev) => addDays(prev, viewRange === 'day' ? 1 : 7));
    };
    const handleToday = () => {
        setAnchorDate(startOfDay(new Date()));
    };

    if (isMobile) {
        return (
            <Box sx={{ p: 2 }}>
                <Alert severity="info">{translate('resources.reservations.calendar.mobileHint')}</Alert>
                <Card sx={{ mt: 2 }}>
                    <CardContent>
                        <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                            {translate('resources.reservations.calendar.title')}
                        </Typography>
                        {visible.length === 0 ? (
                            <Alert severity="info">{translate('resources.reservations.calendar.noReservations')}</Alert>
                        ) : (
                            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                                {visible.map((r) => {
                                    const start = getStartDate(r);
                                    const end = getEndDate(r);
                                    return (
                                        <Box
                                            key={r.id}
                                            onClick={() => setSelected(r)}
                                            sx={{
                                                p: 1.5,
                                                borderRadius: 1,
                                                borderLeft: `4px solid ${STATUS_COLORS[r.status] || '#9E9E9E'}`,
                                                backgroundColor: '#FAFAFA',
                                                cursor: 'pointer',
                                                '&:hover': { backgroundColor: '#F5F5F5' },
                                            }}
                                        >
                                            <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                                {getDisplayName(r)}
                                            </Typography>
                                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                {start
                                                    ? `${start.toLocaleDateString()} · ${formatTimeRange(start, end)}`
                                                    : '—'}
                                            </Typography>
                                            {getChargerName(r) && (
                                                <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                                                    {getChargerName(r)}
                                                </Typography>
                                            )}
                                            <Box sx={{ mt: 0.5 }}>
                                                <ReservationStatusChip status={r.status} />
                                            </Box>
                                        </Box>
                                    );
                                })}
                            </Box>
                        )}
                    </CardContent>
                </Card>
                <ReservationDetailsModal reservation={selected} onClose={() => setSelected(null)} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2, gap: 2, flexWrap: 'wrap' }}>
                <CalendarMonth sx={{ color: '#009A75', fontSize: 32 }} />
                <Typography variant="h5" sx={{ fontWeight: 600, flex: 1 }}>
                    {translate('resources.reservations.calendar.title')}
                </Typography>
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={viewRange}
                    onChange={(_, v) => v && setViewRange(v as ViewRange)}
                >
                    <ToggleButton value="day">{translate('resources.reservations.calendar.dayView')}</ToggleButton>
                    <ToggleButton value="week">{translate('resources.reservations.calendar.weekView')}</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            <Card sx={{ mb: 2 }}>
                <CardContent>
                    <Box sx={{ display: 'flex', flexDirection: { xs: 'column', md: 'row' }, gap: 2, alignItems: { xs: 'stretch', md: 'center' } }}>
                        <TextField
                            select
                            label={translate('resources.reservations.fields.siteId')}
                            value={siteId}
                            onChange={(e) => {
                                setSiteId(e.target.value);
                                setChargerId('');
                            }}
                            size="small"
                            variant="outlined"
                            disabled={sitesLoading}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 240, flex: 1 }}
                        >
                            <MenuItem value="">
                                <em style={{ color: '#9E9E9E' }}>
                                    {translate('resources.reservations.calendar.selectSite')}
                                </em>
                            </MenuItem>
                            {(sites || []).map((s: any) => (
                                <MenuItem key={s.id} value={s.id}>{s.name}</MenuItem>
                            ))}
                        </TextField>

                        <TextField
                            select
                            label={translate('resources.reservations.fields.chargerId')}
                            value={chargerId}
                            onChange={(e) => setChargerId(e.target.value)}
                            size="small"
                            variant="outlined"
                            disabled={!siteId || chargersLoading}
                            InputLabelProps={{ shrink: true }}
                            sx={{ minWidth: 240, flex: 1 }}
                        >
                            <MenuItem value="">
                                <em style={{ color: '#9E9E9E' }}>
                                    {translate('resources.reservations.calendar.allChargers')}
                                </em>
                            </MenuItem>
                            {(chargers || []).map((c: any) => (
                                <MenuItem key={c.id} value={c.id}>{c.name}</MenuItem>
                            ))}
                        </TextField>

                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, ml: { md: 'auto' } }}>
                            <Typography variant="body2" sx={{ fontWeight: 500, color: 'text.secondary', minWidth: 180, textAlign: 'center' }}>
                                {rangeLabel}
                            </Typography>
                            <IconButton onClick={handlePrev} title="Prev" size="small">
                                <ChevronLeft />
                            </IconButton>
                            <IconButton onClick={handleToday} title={translate('resources.reservations.calendar.today')} size="small">
                                <Today />
                            </IconButton>
                            <IconButton onClick={handleNext} title="Next" size="small">
                                <ChevronRight />
                            </IconButton>
                        </Box>
                    </Box>
                    <Box sx={{ mt: 2 }}>
                        <Legend />
                    </Box>
                </CardContent>
            </Card>

            {!siteId ? (
                <Alert severity="info">{translate('resources.reservations.calendar.selectSite')}</Alert>
            ) : (
                <>
                    {!isLoading && visible.length === 0 && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            {translate('resources.reservations.calendar.noReservations')}
                        </Alert>
                    )}
                    <Card sx={{ overflow: 'auto', position: 'relative' }}>
                        {isLoading && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    backgroundColor: 'rgba(255,255,255,0.7)',
                                    zIndex: 10,
                                }}
                            >
                                <CircularProgress />
                            </Box>
                        )}
                        <CardContent>
                            <Box sx={{ display: 'flex', minWidth: 600 }}>
                                {/* Hour column */}
                                <Box sx={{ width: 60, flexShrink: 0 }}>
                                    <Box sx={{ height: 40, borderBottom: '1px solid #E0E0E0' }} />
                                    {Array.from({ length: TIMELINE_HOURS }).map((_, h) => (
                                        <Box
                                            key={h}
                                            sx={{
                                                height: ROW_HEIGHT,
                                                borderBottom: '1px dashed #E0E0E0',
                                                fontSize: '0.7rem',
                                                color: 'text.secondary',
                                                px: 0.5,
                                            }}
                                        >
                                            {fmtTime(new Date(2000, 0, 1, h))}
                                        </Box>
                                    ))}
                                </Box>
                                {/* Day columns */}
                                {days.map((d) => {
                                    const k = dayKey(d);
                                    return (
                                        <Box
                                            key={k}
                                            sx={{
                                                flex: 1,
                                                minWidth: 100,
                                                borderLeft: '1px solid #E0E0E0',
                                            }}
                                        >
                                            <Box
                                                sx={{
                                                    height: 40,
                                                    borderBottom: '1px solid #E0E0E0',
                                                    px: 0.5,
                                                    py: 1,
                                                    backgroundColor: '#F5F5F5',
                                                    textAlign: 'center',
                                                    fontWeight: 600,
                                                    fontSize: '0.75rem',
                                                }}
                                            >
                                                {d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                            </Box>
                                            {Array.from({ length: TIMELINE_HOURS }).map((_, h) => (
                                                <Box
                                                    key={h}
                                                    sx={{
                                                        position: 'relative',
                                                        height: ROW_HEIGHT,
                                                        borderBottom: '1px dashed #E0E0E0',
                                                    }}
                                                >
                                                    {(blocksByDayHour[k]?.[h] || []).map((r) => (
                                                        <Block key={r.id} reservation={r} onOpen={setSelected} />
                                                    ))}
                                                </Box>
                                            ))}
                                        </Box>
                                    );
                                })}
                            </Box>
                        </CardContent>
                    </Card>
                </>
            )}
            <ReservationDetailsModal reservation={selected} onClose={() => setSelected(null)} />
        </Box>
    );
};

export default CalendarView;
